import {useCallback, useEffect, useMemo} from "react";
import {useWindowSize} from "@components/window-size-provider";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import {Button, Checkbox, Col, Form, Input, Layout, Row, Select} from "antd";
import {useRegisterUserMutation} from "@mwServices/Account";
import {Helmet as Head} from "react-helmet-async";
import {Contain} from "@components/layout";
import {getWordpressUrl} from "@lib/wordpressVars";
import sign_up_header_img from "@images/sign_up_header.png";
import {CoverPriceLogoIcon} from "@components/icons/HomePageIcons";
import { CPDatePicker } from "@components/cp-date-picker";

const SignUp = ({ }) => {
    const windowDimensions = useWindowSize();
    const isLoggedIn = useIsLoggedIn();
    const [form] = Form.useForm();
    const [registerUser] = useRegisterUserMutation();

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    useEffect(() => {
        if (isLoggedIn) {
            window.location.href = '/profile';
        }
    }, [isLoggedIn]);

    const onFormSubmit = useCallback(async (values: any) => {
        const sanitizedValues = {
            ...values,
            dob: values.dob.format('YYYY-MM-DD'),
        };

        const userData = await registerUser(sanitizedValues) as any;

        if (userData && userData.data) {
            if (userData.data.success === 1) {
                form.resetFields();
                window.location.href = userData.data.redirect_url;
            } else {
                if (userData.data.error === 'already_logged_in') {
                    window.location.href = '/profile';
                } else {
                    form.setFields([
                        {
                            name: userData.data.form_name,
                            errors: [userData.data.error_message],
                        },
                    ]);
                }
            }
        }
    }, []);

    return (
        <Layout className="page sign-up-page">
            <Head>
                <title>CovrPrice - Sign Up.</title>
            </Head>
            <Contain>
                <Row className="top-box flex-align-center flex-vertical-center" style={{
                    background: `url(${getWordpressUrl(sign_up_header_img)}) no-repeat center center`,
                }}>
                    <div className="top-box-content">
                        <CoverPriceLogoIcon className="top-box-icon"/>
                        <p className="top-title">No Credit Card Needed, No Obligation.</p>
                        <p className="top-subitle">After 5 days your account will automatically revert to our Basic
                            (Free) membership.</p>
                    </div>
                </Row>

                <Row >
                    {showDesktopView && <Col span={6}/>}
                    <Col span={showDesktopView ? 12 : 24} className="trial-box">
                        <p className="trial-box-title">Start Your <span className="text-member-blue">FREE</span> 5 Day Trial</p>
                        <p className="trial-box-subtitle">Want to skip the Free Trail and sign up now? View our <a href={"/plans/unlimited"} >Membership Options & Prices</a>.</p>
                    </Col>
                    {showDesktopView && <Col span={6}/>}
                </Row>

                <Row className="form-box">
                    {showDesktopView && <Col span={6}/>}
                    <Col span={showDesktopView ? 12 : 24}>
                        <Form
                            layout="vertical"
                            form={form}
                            initialValues={{
                                first_name: '',
                                last_name: '',
                                username: '',
                                email: '',
                                dob: null,
                                gender: 'u',
                                password: '',
                                password_confirm: '',
                                terms_of_service: false,
                                privacy_policy: false,
                                meprmailchimptags_opt_in: true,
                                heard_from: '',
                            }}
                            onFinish={onFormSubmit}
                        >
                            <Row gutter={10}>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item
                                        label="First Name"
                                        name="first_name"
                                        rules={[{required: true, message: 'Please input your first name.'}]}
                                    >
                                        <Input className="width-100 input-text"/>
                                    </Form.Item>
                                </Col>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item
                                        label="Last Name"
                                        name="last_name"
                                        rules={[{required: true, message: 'Please input your last name.'}]}
                                    >
                                        <Input className="width-100 input-text"/>
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        label="Create A Username"
                                        name="username"
                                        rules={[{required: true, message: 'Please input your username.'}]}
                                    >
                                        <Input className="width-100 input-text"/>
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        label="Your Email"
                                        name="email"
                                        rules={[{required: true, message: 'Please input your email.'}]}
                                    >
                                        <Input className="width-100 input-text"/>
                                    </Form.Item>
                                </Col>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item
                                        label="Your Date of Birth"
                                        name="dob"
                                        rules={[{required: true, message: 'Please input your date of birth.'}]}
                                    >
                                        <CPDatePicker className="width-100 input-date-picker" />
                                    </Form.Item>
                                </Col>

                                <Col span={showDesktopView ? 12 : 24}></Col>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item label="Your Gender" name="gender">
                                        <Select className="input-select">
                                            <Select.Option value="u">Unspecified</Select.Option>
                                            <Select.Option value="m">Male</Select.Option>
                                            <Select.Option value="f">Female</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={showDesktopView ? 12 : 24}></Col>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item
                                        label="Create A Password"
                                        name="password"
                                        rules={[
                                            {required: true, message: 'Please input your password.'},
                                            ({getFieldValue}) => ({
                                                validator(_, value) {
                                                    //must have lower case
                                                    if (value && !/(?=.*[a-z])/.test(value)) {
                                                        return Promise.reject(new Error('Your password must contain a lower case letter.'));
                                                    }
                                                    //must have upper case
                                                    if (value && !/(?=.*[A-Z])/.test(value)) {
                                                        return Promise.reject(new Error('Your password must contain an upper case letter.'));
                                                    }
                                                    //must have number
                                                    if (value && !/(?=.*[0-9])/.test(value)) {
                                                        return Promise.reject(new Error('Your password must contain a number.'));
                                                    }
                                                    //must have special character
                                                    if (value && !/(?=.*[^\w\s])/.test(value)) {
                                                        return Promise.reject(new Error('Your password must contain a special character.'));
                                                    }
                                                    //must be between 8 & 20 chars long
                                                    if (value && !/(?=.{8,20})/.test(value)) {
                                                        return Promise.reject(new Error('Your password must contain be between 8 and 20 characters long.'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input className="width-100 input-text" type="password"/>
                                    </Form.Item>
                                </Col>
                                <Col span={showDesktopView ? 12 : 24}>
                                    <Form.Item
                                        label="Confirm Password"
                                        name="password_confirm"
                                        rules={[
                                            {required: true, message: 'Please confirm your password.'},
                                            ({getFieldValue}) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('The two passwords that you entered do not match.'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input className="width-100 input-text" type="password"/>
                                    </Form.Item>
                                </Col>
                                <Col span={24} className={"text-grey mar-bot-10"}>
                                    Must be at least 8-20 chars long and contain at least 1 uppercase, 1 lowercase, 1 number, 1 special char.
                                </Col>

                                <Col span={24}>
                                    <Form.Item label="How did you hear about CovrPrice?" name="heard_from">
                                        <Select className="input-select">
                                            <Select.Option value="">Please Select..</Select.Option>
                                            <Select.Option value="friend">From a friend</Select.Option>
                                            <Select.Option value="website">Another website (e.g.
                                                comicbook.com)</Select.Option>
                                            <Select.Option value="youtube">YouTube</Select.Option>
                                            <Select.Option value="instagram">Instagram</Select.Option>
                                            <Select.Option value="facebook">Facebook</Select.Option>
                                            <Select.Option value="reddit">Reddit</Select.Option>
                                            <Select.Option value="tiktok">TikTok</Select.Option>
                                            <Select.Option value="other">Other</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={24}>
                                    <Form.Item
                                        name="terms_of_service"
                                        valuePropName="checked"
                                        rules={[{
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject(new Error('Please agree to the Terms of Service.')),
                                        }]}
                                        className="mar-0"
                                    >
                                        <Checkbox className={"mar-top-10"}>I have read and agree to the <a
                                            href="/terms-of-use/">Terms of Service</a></Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="privacy_policy"
                                        valuePropName="checked"
                                        rules={[{
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject(new Error('Please agree to the Privacy Policy.')),
                                        }]}
                                        className="mar-0"
                                    >
                                        <Checkbox className={"mar-top-10"}>I have read and agree to the <a
                                            href="/privacy-policy/">Privacy Policy</a></Checkbox>
                                    </Form.Item>

                                    <Form.Item
                                        name="meprmailchimptags_opt_in"
                                        valuePropName="checked"
                                        className="mar-0"
                                    >
                                        <Checkbox className={"mar-top-10"}>Sign up for CovrPrice newsletters, comic
                                            reports & system news.</Checkbox>
                                    </Form.Item>
                                </Col>

                                <Col span={24} className={"flex-align-center mar-top-10 "}>
                                    <Button className="orangeButton width-250px" size="large"
                                            onClick={() => form.submit()}>Sign Up</Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                    {showDesktopView && <Col span={6}/>}
                </Row>
            </Contain>
        </Layout>
    );
};

export default SignUp;
